import Image from 'next/image';

import { Box, Flex, FlexProps, Heading, Link, SimpleGrid, Text } from '@chakra-ui/react';

export type SchoolEarningsBandStat = {
  prefix?: string;
  value: string;
  suffix?: string;
  label: string;
};

export type SchoolEarningsBandProps = {
  title: string;
  stats: SchoolEarningsBandStat[];
  disclaimer: string;
  sources: Array<{ label: string; href?: string }>;
  theme: {
    gradient: string;
    textureImage: string;
    statRuleColor: string;
    disclaimerColor: string;
  };
};

export function SchoolEarningsBand({ title, stats, disclaimer, sources, theme }: SchoolEarningsBandProps) {
  return (
    <Flex alignItems='center' flexDirection='column' gap='24px' width='100%'>
      <Flex
        alignSelf={{ base: 'stretch', md: 'center' }}
        bgGradient={theme.gradient}
        borderRadius={{ base: '0', md: '12px' }}
        flexDirection='column'
        gap={{ base: '24px', md: '32px' }}
        justifyContent='space-between'
        marginX={{ base: '-16px', md: '0' }}
        overflow='hidden'
        padding={{ base: '24px', md: '40px 40px 48px' }}
        position='relative'
        width={{ base: 'auto', md: '100%' }}
      >
        <Box
          height={{ base: '216px', md: '288px' }}
          opacity={0.1}
          position='absolute'
          right={0}
          top={0}
          width={{ base: '216px', md: '288px' }}
          aria-hidden
        >
          <Image alt='' sizes='288px' src={theme.textureImage} fill />
        </Box>
        <Heading
          as='h3'
          color='white'
          fontSize={{ base: '1.75rem', md: '2.25rem' }}
          letterSpacing='-0.01em'
          lineHeight={{ base: '1.15', md: '1.1' }}
          position='relative'
        >
          {title}
        </Heading>
        <Flex flexDirection='column' gap='8px' position='relative'>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: '8px', md: '16px' }}>
            {stats.map((stat, index) => (
              <EarningsStatCard
                key={stat.label}
                ruleColor={theme.statRuleColor}
                showSourceNumber={index < sources.length}
                sourceNumber={index + 1}
                stat={stat}
              />
            ))}
          </SimpleGrid>
          <Text color={theme.disclaimerColor} fontSize='0.625rem' fontStyle='italic' lineHeight='1.4'>
            {disclaimer}
          </Text>
        </Flex>
      </Flex>
      {sources.length > 0 && (
        <Flex flexWrap='wrap' gap='24px' justifyContent='center'>
          {sources.map((source, index) => (
            <Flex key={source.label} alignItems='center' gap='6px'>
              <SourceNumberBadge color='grey.900' number={index + 1} />
              {source.href ? (
                <Link
                  color='grey.500'
                  fontSize='0.625rem'
                  href={source.href}
                  lineHeight='1.4'
                  textDecoration='underline'
                  isExternal
                >
                  {source.label}
                </Link>
              ) : (
                <Text color='grey.500' fontSize='0.625rem' lineHeight='1.4'>
                  {source.label}
                </Text>
              )}
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
}

function EarningsStatCard({
  stat,
  ruleColor,
  sourceNumber,
  showSourceNumber,
}: {
  stat: SchoolEarningsBandStat;
  ruleColor: string;
  sourceNumber: number;
  showSourceNumber: boolean;
}) {
  return (
    <Flex
      backdropFilter='blur(8px)'
      bg='rgba(255, 255, 255, 0.16)'
      border='1px solid'
      borderColor='rgba(255, 255, 255, 0.24)'
      borderRadius={{ base: '4px', md: '8px' }}
      flexDirection='column'
      padding={{ base: '16px', md: '28px' }}
      position='relative'
    >
      <Flex
        alignItems={{ base: 'center', md: 'stretch' }}
        borderLeft='2px solid'
        borderLeftColor={ruleColor}
        flexDirection={{ base: 'row', md: 'column' }}
        gap={{ base: '16px', md: '12px' }}
        justifyContent='center'
        paddingLeft={{ base: '12px', md: '20px' }}
      >
        <Text color='white' flexShrink={0} fontFamily='heading' fontWeight='600' lineHeight='1' whiteSpace='nowrap'>
          {stat.prefix && (
            <Box as='span' fontSize={{ base: '1.375rem', md: '1.625rem' }}>
              {stat.prefix}
            </Box>
          )}
          <Box as='span' fontSize={{ base: '2rem', md: '2.375rem' }}>
            {stat.value}
          </Box>
          {stat.suffix && (
            <Box as='span' fontSize={{ base: '1.375rem', md: '1.625rem' }}>
              {stat.suffix}
            </Box>
          )}
        </Text>
        <Flex alignItems='flex-end' flex={{ base: 1, md: 'none' }} gap='8px' minW={0}>
          <Text color='white' flex={1} fontSize={{ base: '0.75rem', md: '0.8125rem' }} lineHeight='1.4'>
            {stat.label}
          </Text>
          {showSourceNumber && (
            <SourceNumberBadge
              bottom={{ base: '11px', md: 'auto' }}
              color='white'
              number={sourceNumber}
              position={{ base: 'absolute', md: 'static' }}
              right={{ base: '11px', md: 'auto' }}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}

function SourceNumberBadge({
  color,
  number,
  ...rest
}: { color: string; number: number } & Omit<FlexProps, 'color' | 'children'>) {
  return (
    <Flex
      alignItems='center'
      border='1px solid'
      borderColor={color}
      borderRadius='full'
      boxSize='12px'
      flexShrink={0}
      justifyContent='center'
      aria-hidden
      {...rest}
    >
      <Text color={color} fontSize='7px' fontWeight='700' lineHeight='1'>
        {number}
      </Text>
    </Flex>
  );
}
